import { Suspense, type ReactNode } from "react";
import { Await, useAsyncError } from "react-router-dom";
import { Skeleton } from "@steelyard/theme";

/**
 * 008-reporting-dashboards T007/T008 (FR-002, FR-005). One `<Suspense>` + one `<Await>` per tile,
 * fed a promise the loader started **without awaiting it** (`dashboard.tsx`'s `dashboardLoader`) —
 * that is what makes one slow service unable to hold up every other tile: the route itself never
 * awaits these promises, so `useLoaderData()` returns before any of them settle and each tile
 * resolves independently as its own promise does.
 *
 * `errorElement` is `Await`'s own per-promise catch, not a route error boundary — a rejected
 * promise renders through here as this tile's own "unreadable" state (FR-002), which is the tile
 * working correctly. That is a different failure mode from the tile's *component* throwing while
 * rendering a resolved value, which is what a route-level `errorElement` (each `/reports/*` route's
 * own `errorElement={<AppError />}`) exists to catch; nothing here duplicates that.
 */
function CaughtError({ render }: { render: (error: unknown) => ReactNode }) {
  // 008-reporting-dashboards T036 (FR-023): `Await`'s own hook, only readable from a component it
  // renders as `errorElement` — this is what lets each tile's `unreadable()` show the caught
  // rejection's own problem document instead of a fixed, invented sentence.
  const error = useAsyncError();
  return <>{render(error)}</>;
}

export function TileBoundary<T>({
  resolve,
  errorFallback,
  fallbackClassName = "h-[140px]",
  children,
}: {
  resolve: Promise<T>;
  errorFallback: (error: unknown) => ReactNode;
  /** The loading skeleton's height, shaped like the tile it stands in for so nothing jumps on arrival. */
  fallbackClassName?: string;
  children: (value: T) => ReactNode;
}) {
  return (
    <Suspense fallback={<Skeleton className={`${fallbackClassName} w-full rounded-xl`} />}>
      <Await resolve={resolve} errorElement={<CaughtError render={errorFallback} />}>
        {(value) => children(value as T)}
      </Await>
    </Suspense>
  );
}
